import React, { useEffect, useRef } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { UserSessionSelector } from './store/UserSession';
import { GuideProvider } from "./utils/contexts/GuideContext";
import { markInAppNavigation } from './utils/navigation/safeBackNavigation';

import Login from './pages/shared/Login';
import Signup from './pages/shared/Signup';
import ResetPassword from './pages/shared/ResetPassword.js';
import SignupSuccess from './pages/shared/SucessSignup';
import AccountActivation from './pages/shared/AccountActivation';
import AccountActivationError from './pages/shared/AccountActivationError';
import ApproveAdminClientJoinRequest from './pages/shared/ApproveAdminClientJoinRequest';

import AdminClientMainMenu from './components/AdminClient/AdminClientMainMenu';
import WriterMainMenu from './components/Writer/WriterMainMenu';
import ConsultantMainMenu from './components/Consultant/ConsultantMainMenu';
import ClientMainMenu from './components/Client/ClientMainMenu';
import WriterClientMainMenu from "./components/WriterClient/WriterClientMainMenu";
import AdminMainMenu from 'components/Admin/AdminMainMenu';
import ProjectManagerMainMenu from 'components/ProjectManager/ProjectManagerMainMenu';
import HelpPanel from './components/shared/UseCasePanel';
import NewVideosLoginModal from './components/shared/NewVideosLoginModal';


import { ROLES } from './utils/Constants';

import './App.css';

function InAppNavigationTracker() {
    const location = useLocation();
    const isFirstLocationRef = useRef(true);

    useEffect(() => {
        // First entry in this tab (e.g. pasted deep link) must NOT count as in-app nav.
        // Any later route change means the user moved inside the app in this tab.
        if (isFirstLocationRef.current) {
            isFirstLocationRef.current = false;
            return;
        }
        markInAppNavigation();
    }, [location]);

    return null;
}

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

const AUTH_PUBLIC_PATHS = [
    '/login',
    '/signup',
    '/SucessSignup',
    '/resetpassword',
    '/account-activation',
    '/account-activation-error',
    '/approve-admin-client-join-request',
];

function isAuthPublicPath(pathname) {
    return AUTH_PUBLIC_PATHS.some(
        (path) => pathname === path || pathname.startsWith(`${path}/`)
    );
}

function isSafeInternalPath(fullPath) {
    if (!fullPath || !fullPath.startsWith('/') || fullPath.startsWith('//')) {
        return false;
    }
    const pathname = fullPath.split('?')[0].split('#')[0];
    return Boolean(pathname) && !isAuthPublicPath(pathname);
}

function getFullPath(location) {
    if (!location?.pathname) return null;
    return `${location.pathname}${location.search || ''}${location.hash || ''}`;
}

function savePostLoginRedirect(location) {
    const fullPath = getFullPath(location);
    if (!isSafeInternalPath(fullPath)) return;

    try {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, fullPath);
    } catch {

    }
}

function readStoredPostLoginRedirect() {
    try {
        const saved = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
        return isSafeInternalPath(saved) ? saved : null;
    } catch {
        return null;
    }
}

function clearStoredPostLoginRedirect() {
    try {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    } catch {

    }
}

function PostLoginRedirect({ defaultPath }) {
    const location = useLocation();

    const isPostAuthLanding = location.pathname === '/login' || location.pathname === '/';

    const fromPath = getFullPath(location.state?.from);
    const storedPath = readStoredPostLoginRedirect();
    const redirectTo = !isPostAuthLanding
        ? defaultPath
        : isSafeInternalPath(fromPath)
            ? fromPath
            : storedPath || defaultPath;

    useEffect(() => {
        if (isPostAuthLanding && redirectTo !== defaultPath) {
            clearStoredPostLoginRedirect();
            try {
                sessionStorage.setItem('cameFromLogin', '1');
            } catch {
                // Ignore storage errors.
            }
        }
    }, [isPostAuthLanding, redirectTo, defaultPath]);

    if (redirectTo === defaultPath) {
        return <Redirect to={defaultPath} />;
    }

    const pathMatch = String(redirectTo).match(/^([^?#]*)(\?[^#]*)?(#.*)?$/);
    return (
        <Redirect
            to={{
                pathname: pathMatch?.[1] || redirectTo,
                search: pathMatch?.[2] || '',
                hash: pathMatch?.[3] || '',
                state: { ...(location.state || {}), fromLogin: true },
            }}
        />
    );
}

function ScrollLockCleanup() {
    const { pathname } = useLocation();

    useEffect(() => {
        const body = document.body;
        if (!body) return undefined;
        const isDebugEnabled = window.localStorage.getItem('scrollLockDebug') === '1';

        const getStateSnapshot = () => ({
            pathname: window.location.pathname,
            modalOpenClass: body.classList.contains('modal-open'),
            bodyOverflow: body.style.overflow || '(empty)',
            bodyPaddingRight: body.style.paddingRight || '(empty)',
            visibleModals: document.querySelectorAll('.modal.show').length,
            ariaModalModals: document.querySelectorAll('.modal[aria-modal="true"]').length,
            backdrops: document.querySelectorAll('.modal-backdrop').length,
        });

        const debugLog = (event, extra = {}) => {
            if (!isDebugEnabled) return;
            // Temporary runtime diagnostics to identify which modal flow leaves stale lock.
            console.info('[scroll-lock-debug]', { event, ...getStateSnapshot(), ...extra });
        };

        const unlockIfStale = () => {
            const hasOpenModal =
                document.querySelector('.modal.show') ||
                document.querySelector('.modal[aria-modal="true"]');
            const hasBackdrop = document.querySelector('.modal-backdrop');
            const isBodyLocked =
                body.classList.contains('modal-open') || body.style.overflow === 'hidden';

            if (isBodyLocked) {
                debugLog('locked-state-detected');
            }

            if (isBodyLocked && !hasOpenModal && !hasBackdrop) {
                debugLog('stale-lock-cleanup-before');
                body.classList.remove('modal-open');
                body.style.overflow = '';
                body.style.paddingRight = '';
                debugLog('stale-lock-cleanup-after');
            }
        };

        unlockIfStale();

        const observer = new MutationObserver(() => {
            unlockIfStale();
        });

        observer.observe(document.documentElement, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style', 'aria-hidden'],
        });

        window.addEventListener('focus', unlockIfStale);
        window.addEventListener('pageshow', unlockIfStale);
        document.addEventListener('visibilitychange', unlockIfStale);

        return () => {
            observer.disconnect();
            window.removeEventListener('focus', unlockIfStale);
            window.removeEventListener('pageshow', unlockIfStale);
            document.removeEventListener('visibilitychange', unlockIfStale);
        };
    }, []);

    useEffect(() => {
        const body = document.body;
        if (!body) return;
        const isDebugEnabled = window.localStorage.getItem('scrollLockDebug') === '1';
        const routeState = {
            pathname,
            modalOpenClass: body.classList.contains('modal-open'),
            bodyOverflow: body.style.overflow || '(empty)',
            backdrops: document.querySelectorAll('.modal-backdrop').length,
            visibleModals: document.querySelectorAll('.modal.show').length,
        };

        if (isDebugEnabled) {
            console.info('[scroll-lock-debug]', { event: 'route-change-before-cleanup', ...routeState });
        }

        body.classList.remove('modal-open');
        body.style.overflow = '';
        body.style.paddingRight = '';

        const staleBackdrops = document.querySelectorAll('.modal-backdrop');
        staleBackdrops.forEach((backdrop) => backdrop.remove());

        if (isDebugEnabled) {
            console.info('[scroll-lock-debug]', {
                event: 'route-change-after-cleanup',
                pathname,
                modalOpenClass: body.classList.contains('modal-open'),
                bodyOverflow: body.style.overflow || '(empty)',
                backdrops: document.querySelectorAll('.modal-backdrop').length,
                visibleModals: document.querySelectorAll('.modal.show').length,
            });
        }
    }, [pathname]);

    return null;
}
function ConditionalHelpPanel() {
    const { pathname } = useLocation();

    // Pages where HelpPanel should NOT appear
    const excludedPaths = [
        '/login',
        '/logout',
        '/signup',
        '/resetpassword',
        '/MonProfil',
        '/AdminClient/CreateTraductionIA',
    ];

    const shouldHide = excludedPaths.some(path =>
        pathname.toLowerCase().includes(path.toLowerCase())
    );

    return shouldHide ? null : <HelpPanel />;
}

export default function App() {
    const { user } = useSelector(UserSessionSelector);

    let content; // 👈 on va remplir ça selon le rôle

    if (user && user.role === ROLES.ADMIN_CLIENT) {
        content = (
            <Switch>
                <Route path="/account-activation" component={AccountActivation} />
                <Route path="/account-activation-error" component={AccountActivationError} />
                <Route path="/approve-admin-client-join-request" component={ApproveAdminClientJoinRequest} />
                <Route
                    exact
                    path="/"
                    render={() => <Redirect to="/AdminClient/AdminMainMenu" />}
                />
                <Route path="/AdminClient" component={AdminClientMainMenu} />
                <PostLoginRedirect defaultPath="/AdminClient/AdminMainMenu" />
            </Switch>
        );
    } else if (user && user.role === ROLES.GLOBAL_WRITER) {
        content = (
            <Switch>
                <Route path="/account-activation" component={AccountActivation} />
                <Route path="/account-activation-error" component={AccountActivationError} />
                <Route path="/approve-admin-client-join-request" component={ApproveAdminClientJoinRequest} />
                <Route
                    exact
                    path="/"
                    render={() => <Redirect to="/WriterClient/WriterMainMenu" />}
                />
                <Route path="/WriterClient" component={WriterClientMainMenu} />
                <PostLoginRedirect defaultPath="/WriterClient/WriterMainMenu" />
            </Switch>
        );
    } else if (user && user.role === ROLES.ADMIN) {
        content = (
            <Switch>
                <Route path="/account-activation" component={AccountActivation} />
                <Route path="/account-activation-error" component={AccountActivationError} />
                <Route path="/approve-admin-client-join-request" component={ApproveAdminClientJoinRequest} />
                <Route
                    exact
                    path="/"
                    render={() => <Redirect to="/Admin/AdminMainMenu" />}
                />
                <Route path="/Admin" component={AdminMainMenu} />
                <PostLoginRedirect defaultPath="/Admin/AdminMainMenu" />
            </Switch>
        );
    } else if (user && user.role === ROLES.PROJECT_MANAGER) {
        content = (
            <Switch>
                <Route path="/account-activation" component={AccountActivation} />
                <Route path="/account-activation-error" component={AccountActivationError} />
                <Route path="/approve-admin-client-join-request" component={ApproveAdminClientJoinRequest} />
                <Route
                    exact
                    path="/"
                    render={() => <Redirect to="/ProjectManager/MainMenu" />}
                />
                <Route path="/ProjectManager" component={ProjectManagerMainMenu} />
                <PostLoginRedirect defaultPath="/ProjectManager/MainMenu" />
            </Switch>
        );
    } else if (user && user.role === ROLES.WRITER) {
        content = (
            <Switch>
                <Route path="/account-activation" component={AccountActivation} />
                <Route path="/account-activation-error" component={AccountActivationError} />
                <Route path="/approve-admin-client-join-request" component={ApproveAdminClientJoinRequest} />
                <Route
                    exact
                    path="/"
                    render={() => <Redirect to="/Redacteur/MainMenu" />}
                />
                <Route path="/Redacteur" component={WriterMainMenu} />
                <PostLoginRedirect defaultPath="/Redacteur/MainMenu" />
            </Switch>
        );
    } else if (user && user.role === 'CONSULTANT') {
        content = (
            <Switch>
                <Route path="/account-activation" component={AccountActivation} />
                <Route path="/account-activation-error" component={AccountActivationError} />
                <Route path="/approve-admin-client-join-request" component={ApproveAdminClientJoinRequest} />
                <Route
                    exact
                    path="/"
                    render={() => <Redirect to="/Consultant/MainMenu" />}
                />
                <Route path="/Consultant" component={ConsultantMainMenu} />
                <PostLoginRedirect defaultPath="/Consultant/MainMenu" />
            </Switch>
        );
    } else if (user && user.role === 'CLIENT') {
        content = (
            <Switch>
                <Route path="/account-activation" component={AccountActivation} />
                <Route path="/account-activation-error" component={AccountActivationError} />
                <Route path="/approve-admin-client-join-request" component={ApproveAdminClientJoinRequest} />
                <Route
                    exact
                    path="/"
                    render={() => <Redirect to="/Client/MainMenu" />}
                />
                <Route path="/Client" component={ClientMainMenu} />
                <PostLoginRedirect defaultPath="/Client/MainMenu" />
            </Switch>
        );
    } else {
        // pas connecté
        content = (
            <div>
                <Switch>
                    <Route path="/account-activation" component={AccountActivation} />
                    <Route path="/account-activation-error" component={AccountActivationError} />
                    <Route path="/approve-admin-client-join-request" component={ApproveAdminClientJoinRequest} />
                    <Route path="/login" component={Login} />
                    <Route path="/resetpassword" component={ResetPassword} />
                    <Route path="/signup" component={Signup} />
                    <Route path="/SucessSignup" component={SignupSuccess} />
                    <Route
                        path="*"
                        render={({ location }) => {
                            savePostLoginRedirect(location);
                            return (
                                <Redirect
                                    to={{
                                        pathname: '/login',
                                        state: { from: location },
                                    }}
                                />
                            );
                        }}
                    />
                </Switch>
            </div>
        );
    }

    // 👇 Ici on wrap tout avec le GuideProvider
    return (
        <GuideProvider>
            <InAppNavigationTracker />
            <ScrollLockCleanup />
            {content}
            {user && <ConditionalHelpPanel />}
            {user && <NewVideosLoginModal />}
        </GuideProvider>
    );
}
